import type { TimelineStage } from "../components/DebateTimeline";
import { expertOptions } from "../constants/experts";

/** Roles that take part in a debate without being a selectable expert. */
const ROLE_LABELS: Record<string, string> = {
  chaos: "Chaos agent",
  analyst: "Analyst",
  fact_checker: "Fact checker",
  framer: "Framer",
  researcher: "Researcher",
};

/** Human-readable name for an expert or debate role, e.g. "creative" → "Creative Director". */
export function expertLabel(key: string): string {
  return (
    expertOptions.find((e) => e.value === key)?.label ??
    ROLE_LABELS[key] ??
    key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")
  );
}

export function joinNames(names: string[]) {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

interface StageInput {
  councilProgress: any[];
  chaosProgress: any[];
  councilFinal: string | null;
  facilitatorDone: boolean;
  isLoading: boolean;
  selectedExperts: string[];
}

export function buildSingleStages({ councilProgress, chaosProgress, isLoading }: StageInput): TimelineStage[] {
  if (councilProgress.length === 0 && !isLoading) return [];
  const has = (step: string) => councilProgress.some((p) => p.step === step);
  const phases = [
    { key: "frame", label: "Framing", expert: "framer", step: "frame" },
    { key: "research", label: "Research", expert: "researcher", step: "research_gather" },
    { key: "initial", label: "Expert analysis", expert: "analyst", step: "initial" },
    { key: "chaos", label: "Chaos challenge" },
    { key: "fact_check", label: "Fact check", expert: "fact_checker", step: "fact_check" },
    { key: "revision", label: "Final answer", expert: "analyst", step: "revision" },
  ].filter((ph) => (ph.key === "frame" ? has("frame") : ph.key === "research" ? has("research_gather") : true));
  return phases.map((ph) => {
    if (ph.key === "chaos") {
      const c = chaosProgress.find((x) => x.round === 1);
      return { key: ph.key, label: ph.label, status: c?.done ? "done" : c ? "active" : "pending" };
    }
    const entries = councilProgress.filter((x) => x.expert === ph.expert && x.step === ph.step);
    const status = entries.some((x) => x.done === true) ? "done" : entries.length ? "active" : "pending";
    return { key: ph.key, label: ph.label, status };
  });
}

export function buildMultiStages({
  councilProgress,
  chaosProgress,
  councilFinal,
  facilitatorDone,
  selectedExperts: experts,
}: StageInput): TimelineStage[] {
  const stageFor = (key: string, label: string, match: (p: any) => boolean): TimelineStage => {
    const started = experts.filter((e) => councilProgress.some((p) => p.expert === e && match(p)));
    const done = experts.filter((e) => councilProgress.some((p) => p.expert === e && match(p) && p.done === true));
    const writing = started.filter((e) => !done.includes(e)).map(expertLabel);
    const status = experts.length > 0 && done.length === experts.length ? "done" : started.length > 0 ? "active" : "pending";
    return { key, label, status, activity: writing.length ? `${joinNames(writing)} writing` : undefined };
  };
  const stages: TimelineStage[] = [];
  if (councilProgress.some((p) => p.step === "research_gather")) {
    stages.push(stageFor("research", "Research", (p) => p.step === "research_gather"));
  }
  stages.push(stageFor("initial", "Opening arguments", (p) => p.step === "initial"));
  stages.push(stageFor("feedback", "Cross-examination", (p) => p.step === "feedback"));
  const chaos = chaosProgress.find((c) => c.round === 1);
  stages.push({
    key: "chaos",
    label: "Chaos challenge",
    status: chaos?.done ? "done" : chaos ? "active" : "pending",
    activity: chaos && !chaos.done ? "Chaos agent probing weak points" : undefined,
  });
  stages.push(stageFor("revision", "Revised positions", (p) => p.step === "revision"));
  stages.push({
    key: "synthesis",
    label: "Facilitator synthesis",
    status: facilitatorDone ? "done" : councilFinal ? "active" : "pending",
    activity: councilFinal && !facilitatorDone ? "Facilitator writing the decision matrix" : undefined,
  });
  return stages;
}
